/**
 * HTML Formatting Utilities
 * 
 * Functions for formatting and beautifying HTML content
 * Also handles extracting/reconstructing full HTML documents for Quill editor
 */

/**
 * Extract body content from full HTML document
 * Quill only edits body content, so we need to extract it
 */
export function extractBodyContent(html: string): string {
    if (!html || typeof html !== 'string') {
        return html || '';
    }

    const trimmed = html.trim();
    if (!trimmed) {
        return '';
    }

    // If it's already just body content (no html/head tags), return as-is
    if (!trimmed.toLowerCase().includes('<html') && !trimmed.toLowerCase().includes('<!doctype')) {
        return trimmed;
    }

    // Try to extract body content using regex
    const bodyMatch = trimmed.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch && bodyMatch[1]) {
        return bodyMatch[1].trim();
    }

    // If no body tag found, assume the whole thing is body content
    return trimmed;
}

/**
 * Reconstruct full HTML document from body content
 * Preserves html/head tags if they existed in the original
 */
export function reconstructFullHTML(bodyContent: string, originalHTML?: string): string {
    if (!bodyContent || typeof bodyContent !== 'string') {
        return bodyContent || '';
    }

    // If original HTML had full document structure, reconstruct it
    if (originalHTML && typeof originalHTML === 'string') {
        const trimmedOriginal = originalHTML.trim();

        // Check if original had html/head tags
        if (trimmedOriginal.toLowerCase().includes('<html') || trimmedOriginal.toLowerCase().includes('<!doctype')) {
            // Extract head content if it exists
            const headMatch = trimmedOriginal.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
            const htmlMatch = trimmedOriginal.match(/<html[^>]*>/i);
            const doctypeMatch = trimmedOriginal.match(/<!doctype[^>]*>/i);

            let reconstructed = '';

            // Add doctype if it existed
            if (doctypeMatch) {
                reconstructed += doctypeMatch[0] + '\n';
            }

            // Add html tag
            if (htmlMatch) {
                reconstructed += htmlMatch[0] + '\n';
            } else {
                reconstructed += '<html>\n';
            }

            // Add head if it existed
            if (headMatch) {
                reconstructed += '  <head>\n';
                reconstructed += headMatch[1].split('\n').map(line => '    ' + line).join('\n');
                reconstructed += '\n  </head>\n';
            } else {
                reconstructed += '  <head></head>\n';
            }

            // Add body with new content
            reconstructed += '  <body>\n';
            reconstructed += bodyContent.split('\n').map(line => '    ' + line).join('\n');
            reconstructed += '\n  </body>\n';
            reconstructed += '</html>';

            return reconstructed;
        }
    }

    // If no original structure, just return body content as-is
    return bodyContent;
}

/**
 * Format HTML string with proper indentation
 * This is a simple formatter that adds line breaks and indentation to HTML
 */
export function formatHTML(html: string, indentSize: number = 2): string {
    if (!html || typeof html !== 'string') {
        return html || '';
    }

    const trimmed = html.trim();
    if (!trimmed) {
        return '';
    }

    // Simple HTML formatter that handles basic cases
    // For more complex HTML, consider using a library like js-beautify
    let formatted = '';
    let indent = 0;
    let inTag = false;
    let inAttribute = false;
    let inString = false;
    let stringChar = '';
    let currentLine = '';
    let i = 0;

    while (i < trimmed.length) {
        const char = trimmed[i];
        const nextChar = trimmed[i + 1];

        if (char === '<' && !inString) {
            // Closing tag
            if (nextChar === '/') {
                if (currentLine.trim()) {
                    formatted += ' '.repeat(indent * indentSize) + currentLine.trim() + '\n';
                    currentLine = '';
                }
                indent = Math.max(0, indent - 1);
                inTag = true;
                currentLine += char;
            } else {
                // Opening tag
                if (currentLine.trim()) {
                    formatted += ' '.repeat(indent * indentSize) + currentLine.trim() + '\n';
                    currentLine = '';
                }
                inTag = true;
                currentLine += char;
            }
        } else if (char === '>' && !inString) {
            currentLine += char;
            inTag = false;

            // Check if it's a self-closing tag
            const isSelfClosing = /<[^>]+\/>$/.test(currentLine.trim());
            if (!isSelfClosing && !currentLine.trim().startsWith('</')) {
                indent++;
            }

            formatted += ' '.repeat(Math.max(0, indent - (isSelfClosing || currentLine.trim().startsWith('</') ? 0 : 1)) * indentSize) + currentLine.trim() + '\n';
            currentLine = '';
        } else if ((char === '"' || char === "'") && inTag) {
            if (!inString) {
                inString = true;
                stringChar = char;
                currentLine += char;
            } else if (char === stringChar) {
                inString = false;
                stringChar = '';
                currentLine += char;
            } else {
                currentLine += char;
            }
        } else {
            currentLine += char;
        }

        i++;
    }

    // Add any remaining content
    if (currentLine.trim()) {
        formatted += ' '.repeat(indent * indentSize) + currentLine.trim();
    }

    // Clean up: remove excessive blank lines and trim
    return formatted
        .split('\n')
        .map(line => line.trimEnd())
        .filter((line, index, array) => {
            // Remove consecutive blank lines
            if (!line.trim() && index > 0 && !array[index - 1].trim()) {
                return false;
            }
            return true;
        })
        .join('\n')
        .trim();
}

/**
 * Simple HTML formatter using regex (faster but less accurate)
 * Use this for better performance on large HTML strings
 */
export function formatHTMLSimple(html: string, indentSize: number = 2): string {
    if (!html || typeof html !== 'string') {
        return html || '';
    }

    const trimmed = html.trim();
    if (!trimmed) {
        return '';
    }

    // Replace > with >\n and < with \n< to separate tags
    let formatted = trimmed
        .replace(/>/g, '>\n')
        .replace(/</g, '\n<')
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .join('\n');

    // Add indentation
    let indent = 0;
    const lines = formatted.split('\n');
    const indentedLines: string[] = [];

    for (const line of lines) {
        const trimmedLine = line.trim();

        if (trimmedLine.startsWith('</')) {
            // Closing tag - decrease indent before adding
            indent = Math.max(0, indent - 1);
            indentedLines.push(' '.repeat(indent * indentSize) + trimmedLine);
        } else if (trimmedLine.startsWith('<')) {
            // Opening tag
            indentedLines.push(' '.repeat(indent * indentSize) + trimmedLine);
            // Check if it's not a self-closing tag
            if (!trimmedLine.endsWith('/>') && !trimmedLine.match(/^<[^>]+>$/)) {
                indent++;
            }
        } else {
            // Text content
            indentedLines.push(' '.repeat(indent * indentSize) + trimmedLine);
        }
    }

    return indentedLines.join('\n');
}

/**
 * Parse full HTML document into parts: doctype, html tag, head content, body content, closing tags
 */
export function parseHTMLDocument(html: string): {
    doctype: string;
    htmlTag: string;
    headContent: string;
    bodyContent: string;
    closingTags: string;
    isFullDocument: boolean;
} {
    if (!html || typeof html !== 'string') {
        return {
            doctype: '',
            htmlTag: '',
            headContent: '',
            bodyContent: html || '',
            closingTags: '',
            isFullDocument: false
        };
    }

    const trimmed = html.trim();
    if (!trimmed) {
        return {
            doctype: '',
            htmlTag: '',
            headContent: '',
            bodyContent: '',
            closingTags: '',
            isFullDocument: false
        };
    }

    // Check if it's a full HTML document
    const isFullDocument = trimmed.toLowerCase().includes('<html') || trimmed.toLowerCase().includes('<!doctype');

    if (!isFullDocument) {
        // Not a full document, treat entire content as body
        return {
            doctype: '',
            htmlTag: '',
            headContent: '',
            bodyContent: trimmed,
            closingTags: '',
            isFullDocument: false
        };
    }

    // Extract doctype
    const doctypeMatch = trimmed.match(/<!doctype[^>]*>/i);
    const doctype = doctypeMatch ? doctypeMatch[0] : '';

    // Extract html tag
    const htmlMatch = trimmed.match(/<html[^>]*>/i);
    const htmlTag = htmlMatch ? htmlMatch[0] : '<html>';

    // Extract head content
    const headMatch = trimmed.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
    const headContent = headMatch && headMatch[1] ? headMatch[1].trim() : '';

    // Extract body content
    const bodyMatch = trimmed.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    const bodyContent = bodyMatch && bodyMatch[1] ? bodyMatch[1].trim() : '';

    // Extract everything after </body> (closing tags, comments, etc.)
    const bodyEndIndex = trimmed.toLowerCase().indexOf('</body>');
    let closingTags = '';
    if (bodyEndIndex !== -1) {
        const afterBody = trimmed.substring(bodyEndIndex + '</body>'.length);
        closingTags = afterBody.trim();
    }

    return {
        doctype,
        htmlTag,
        headContent,
        bodyContent,
        closingTags,
        isFullDocument: true
    };
}

/**
 * Reconstruct full HTML document from parts
 */
export function reconstructHTMLFromParts(parts: {
    doctype: string;
    htmlTag: string;
    headContent: string;
    bodyContent: string;
    closingTags?: string;
    isFullDocument?: boolean;
}): string {
    if (!parts.isFullDocument) {
        return parts.bodyContent;
    }

    let html = '';

    if (parts.doctype) {
        html += parts.doctype + '\n';
    }

    html += parts.htmlTag + '\n';

    if (parts.headContent) {
        html += '  <head>\n';
        html += parts.headContent.split('\n').map(line => '    ' + line).join('\n');
        html += '\n  </head>\n';
    } else {
        html += '  <head></head>\n';
    }

    html += '  <body>\n';
    html += parts.bodyContent.split('\n').map(line => '    ' + line).join('\n');
    html += '\n  </body>';

    // Add closing tags if they exist
    if (parts.closingTags) {
        html += '\n' + parts.closingTags;
    } else {
        // Default closing tag if none provided
        html += '\n</html>';
    }

    return html;
}
